import type { FormProps } from "antd"
import { Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useCallback, useEffect, useState } from "react"
import "react-quill/dist/quill.snow.css"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { CategoryOption, FieldType, MediaData, PostData, TagOption } from "~/types/types"
import { checkSlug, fetchPostId, updatePost } from "../services/post"
import Categories from "./Categories"
import FeaturedImage from "./FeaturedImage"
import PostForm from "./PostForm"
import Publish from "./Publish"
import Tags from "./Tags"

const EditPost = () => {
  const [quillValue, setQuillValue] = useState<string>("")
  const [post, setPost] = useState<PostData | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [status, setStatus] = useState("")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [language, setLanguages] = useState("")

  const [selectedImage, setSelectedImage] = useState<MediaData | null>(null)
  const [selectedMetaImage, setSelectedMetaImage] = useState<MediaData | null>(null)
  const [selectedFaqs, setSelectedFaqs] = useState<any[]>([])
  const [faqEnabled, setFaqEnabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const onFinish: FormProps<FieldType>["onFinish"] = async values => {
    const isSlugChanged = values.slug !== post?.attributes?.slug
    if (isSlugChanged) {
      const isSlugUnique = async (slug: string): Promise<boolean> => {
        try {
          const response = await checkSlug(slug)
          return response.data.length === 0
        } catch {
          return false
        }
      }

      const isUnique = await isSlugUnique(values.slug || "")

      if (!isUnique) {
        message.error(t("Slug already exists. Please choose another slug."))
        return
      }
    }
    const dataPayload: any = {
      name: values.name,
      content: values.content,
      excerpt: values.excerpt,
      categories: selectedCategories || [],
      tags: selectedTags || [],
      image: selectedImage || null,
      ...(isSlugChanged && { slug: values.slug }),
      publishedAt:
        status === "Draft" ? null : publishDate || post?.attributes?.publishedAt || new Date().toISOString(),
      SEO: {
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        keywords: values.keywords,
        metaRobots: values.metaRobots,
        metaViewport: values.metaViewport,
        structuredData: typeof values.structuredData === "string" ? JSON.parse(values.structuredData) : {},
      },
      faq_enabled: faqEnabled,
      faqs: faqEnabled ? selectedFaqs.map((faq, index) => ({
        __component: "content.faq",
        question: faq.question,
        answer: faq.answer,
        order: index,
      })) : [],
    }

    try {
      await updatePost(Number(id), { data: dataPayload })
      message.success(t("Post updated successfully!"))

      form.resetFields()
      navigate(Paths.POSTS.LIST.PATH)
    } catch {
      message.error(t("Failed to update post. Please try again."))
    }
  }

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = () => { }

  const handleFetchPost = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await fetchPostId(id)
        setPost(response)
        if (response.attributes.publishedAt) {
          setStatus("Publish")
        } else {
          setStatus("Draft")
        }
        form.setFieldsValue(response.attributes)
        form.setFieldValue("metaTitle", response.attributes.SEO.metaTitle)
        form.setFieldValue("metaDescription", response.attributes.SEO.metaDescription)
        form.setFieldValue("keywords", response.attributes.SEO.keywords)
        form.setFieldValue("metaRobots", response.attributes.SEO.metaRobots)
        form.setFieldValue("metaViewport", response.attributes.SEO.metaViewport)
        form.setFieldValue("structuredData", JSON.stringify(response.attributes.SEO.structuredData))

        setSelectedCategories(response.attributes.categories.data.map((category: CategoryOption) => category.id))
        setSelectedTags(response.attributes.tags.data.map((tag: TagOption) => tag.id))
        setQuillValue(response.attributes.content)
        setLanguages(response.attributes?.locale)
        setPublishDate(response.attributes.publishedAt)
        const image = response.attributes.image?.data
        setSelectedImage(image)
        const metaImage = {
          id: response.attributes.SEO.metaImage?.data.id ?? 0,
          ...response.attributes.SEO.metaImage?.data.attributes,
        }
        setSelectedMetaImage(metaImage)

        setFaqEnabled(response.attributes.faq_enabled || false)
        const faqsRaw = response.attributes.faqs
        const faqsArray = faqsRaw?.data || faqsRaw || []
        const faqsData = faqsArray.map((faq: any) => ({
          id: faq.__component ? String(Date.now() + Math.random()) : String(faq.id),
          question: faq.question || faq.attributes?.question,
          answer: faq.answer || faq.attributes?.answer,
        }))
        setSelectedFaqs(faqsData)


        setLoading(false)
      } catch {
        message.error(t("Error fetching post"))
      }
    },
    [form],
  )

  const onChangeCategories = (checkedValues: string[]) => {
    setSelectedCategories(checkedValues)
  }
  const onChangeTags = (checkedValues: string[]) => {
    setSelectedTags(checkedValues)
  }

  const onChangeImage = (image: MediaData | null) => {
    setSelectedImage(image)
  }

  const onChangeMetaImage = (image: MediaData | null) => {
    setSelectedMetaImage(image)
  }


  const onChangeFaqs = (faqs: any[]) => {
    setSelectedFaqs(faqs)
  }

  const onFaqEnabledChange = (enabled: boolean) => {
    setFaqEnabled(enabled)
  }

  useEffect(() => {
    if (id) {
      handleFetchPost(Number(id))
    }
  }, [id, handleFetchPost])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space>
            <Title level={2}>{t("Edit Post")}</Title>
          </Space>
          <div className="flex justify-between gap-4">
            <PostForm
              form={form}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              quillValue={quillValue}
              onChangeMetaImage={onChangeMetaImage}
              selectedFaqs={selectedFaqs}
              onChangeFaqs={onChangeFaqs}
              faqEnabled={faqEnabled}
              onFaqEnabledChange={onFaqEnabledChange}
            />
            <div className="w-[25%] pt-2">
              <Publish
                onSubmit={form.submit}
                language={language}
                setLanguages={setLanguages}
                isCreateEdit={false}
                setStatus={setStatus}
                status={status}
                setPublishDate={setPublishDate}
                publishDate={publishDate}
              />
              <Categories selectedCategories={selectedCategories} onChangeCategories={onChangeCategories} />
              <Tags selectedTags={selectedTags} onChangeTags={onChangeTags} />
              <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={onChangeImage} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EditPost
